import { EikoLogo } from '@/components/EikoLogo';
import { Button } from '@/components/ui/button';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { useMfaResend } from '@/hooks/useMfaResend';
import { useMfaVerify } from '@/hooks/useMfaVerify';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { z } from 'zod';

const mfaSchema = z.object({
  code: z.string().length(6, 'Le code doit contenir 6 chiffres'),
});

type MfaFormData = z.infer<typeof mfaSchema>;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, filter: 'blur(4px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95, filter: 'blur(8px)' },
  visible: {
    opacity: 1,
    scale: 1,
    filter: 'blur(0px)',
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  },
};

export function VerifyMfaView({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const location = useLocation();
  const state = location.state as {
    challengeId: string;
    email: string;
  } | null;

  const [challengeId, setChallengeId] = useState(state?.challengeId ?? '');
  const { verifyMfa, isLoading } = useMfaVerify();
  const {
    resendCode,
    isLoading: isResending,
    newChallengeId,
  } = useMfaResend();

  useEffect(() => {
    if (newChallengeId) {
      setChallengeId(newChallengeId);
    }
  }, [newChallengeId]);

  const form = useForm<MfaFormData>({
    resolver: zodResolver(mfaSchema),
    defaultValues: { code: '' },
  });

  if (!state) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = (data: MfaFormData) => {
    verifyMfa({ challengeId, code: data.code });
  };

  const handleResend = () => {
    resendCode(challengeId);
    form.reset();
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-stone-50">
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 w-full max-w-[420px] mx-4"
      >
        <div
          className={cn(
            'rounded-2xl border border-stone-200 p-8 sm:p-10',
            'bg-white',
            'shadow-sm',
          )}
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-col"
          >
            {/* Brand */}
            <motion.div variants={itemVariants} className="mb-8 flex justify-center">
              <EikoLogo className="h-14 text-emerald-600" />
            </motion.div>

            {/* Title */}
            <motion.div variants={itemVariants} className="mb-6 text-center">
              <h2 className="text-lg font-medium text-stone-800">
                Vérification de votre identité
              </h2>
              <p className="mt-1.5 text-sm text-stone-500">
                Un code de vérification a été envoyé à{' '}
                <span className="font-medium">{state.email}</span>
              </p>
            </motion.div>

            {/* Form */}
            <div className={cn('grid gap-4', className)} {...props}>
              <form onSubmit={form.handleSubmit(handleSubmit)}>
                <div className="grid gap-4">
                  <motion.div
                    variants={itemVariants}
                    className="flex justify-center"
                  >
                    <Controller
                      name="code"
                      control={form.control}
                      render={({ field }) => (
                        <InputOTP
                          maxLength={6}
                          value={field.value}
                          onChange={field.onChange}
                          disabled={isLoading}
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      )}
                    />
                  </motion.div>
                  {form.formState.errors.code && (
                    <p className="text-sm text-red-400 text-center">
                      {form.formState.errors.code.message}
                    </p>
                  )}
                  <motion.div variants={itemVariants} className="pt-2">
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className={cn(
                        'relative w-full h-11 rounded-lg text-sm font-semibold',
                        'bg-emerald-600 text-white',
                        'hover:bg-emerald-700',
                        'transition-all duration-300',
                        'disabled:opacity-50',
                      )}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Vérification...
                        </>
                      ) : (
                        'Vérifier'
                      )}
                    </Button>
                  </motion.div>
                </div>
              </form>

              {/* Resend */}
              <motion.div variants={itemVariants} className="text-center">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isResending}
                  className="text-sm text-stone-400 transition-colors duration-200 hover:text-emerald-600 disabled:opacity-50"
                >
                  {isResending
                    ? 'Envoi en cours...'
                    : "Vous n'avez pas reçu de code ? Renvoyer"}
                </button>
              </motion.div>
            </div>

            {/* Back to login */}
            <motion.div
              variants={itemVariants}
              className="mt-6 text-center"
            >
              <Link
                to="/login"
                className="text-sm text-stone-400 transition-colors duration-200 hover:text-emerald-600"
              >
                Retour à la connexion
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
